import { CommonRsp, BeforeRequestHook, InitOptions, TRequest, AfterResponseErrorHook } from './typings';
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import humps from 'humps';
import { genToken } from './util';
import mergeWith from 'lodash/mergeWith';
import isArray from 'lodash/isArray';
import { NoReportError } from '../../util/error';

export type NetInitOptions = InitOptions;

export interface NetHook{
  /// 各端外部传入钩子
  beforeRequest?: BeforeRequestHook
}

export interface NetRequestOptions<U> extends AxiosRequestConfig {
  serverName?: string;
  funcName?: string;
  path?: string;
  method?: 'POST' | 'GET';
  data?: U;
  contentType?: string;
  autoHandleError?: boolean; // 是否自动处理错误。默认是，网络模块处理错误并toast提示，否的话交给业务处理
  camelizeResponse?: boolean; // 是否驼峰处理返回的数据
}

export interface INetRequestListReq {
  page?: number;
  pageSize?: number;
  page_size?: number;
}

export interface INetRequestListRsp {
  isEnd?: boolean;
  is_end?: boolean;
  nextPage?: number;
  next_page?: number;
}

export enum NetErrorType {
  clientError = 'clientError', // 客户端出错
  businessError = 'businessError', // 业务错误
  authError = 'authError', // 登录鉴权错误
  serverError = 'serverError', // 后台代码错误
  codeError = 'codeError', // 代码错误
}

/** 接口异常错误 */
export class RspError extends Error {
  msg: string;
  ret: number;
  data: unknown;
  type: NetErrorType;
  config?: AxiosRequestConfig;
  isHandled: boolean; // 错误是否已处理。处理后下游就不再处理

  constructor(options: {
    status: number,
    type: NetErrorType,
    data: unknown,
    msg?: string,
    ret?: number,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    stack?: any,
    url?: string;
    message?: string; // 额外信息
  }) {
    const data = options.data as CommonRsp;
    super(`[RspError] type: ${options.type}, url: ${options.url}, status: ${options.status} ret: ${data?.ret}, msg: ${data?.msg}, message: ${options.message ?? ''}`);
    this.name = 'RspError';
    this.msg = options.msg ?? '';
    this.ret = options.ret ?? -1;
    this.type = options.type;
    this.data = data;
    this.isHandled = false;
    if (options.stack) {
      this.stack = options.stack;
    }
  }
}

function customizer(objValue: Array<unknown>, srcValue: Array<unknown>) {
  if (isArray(objValue)) {
    return objValue.concat(srcValue);
  }
}


class Net {
  static tag = 'Net';

  private static appName = 'knockknock';

  private static http: AxiosInstance = axios.create();

  /** 初始化参数 */
  static init(options: InitOptions): void {
    this.http = axios.create({
      headers: {
        accept: 'application/json',
        ...options.headers,
      },
      baseURL: options.baseURL,
    });
    this.beforeRequest(config => ({
      ...config,
      headers: {
        ...config.headers,
        'G-Token': genToken(options.getTokenKey() ?? ''),
      },
    }));

    this.http.interceptors.response.use((rsp: AxiosResponse<CommonRsp>) => {
      const request = rsp.request as XMLHttpRequest;
      if (rsp.data?.ret === 0) {
        const { data } = rsp;
        const { url, camelizeResponse } = rsp.config as NetRequestOptions<unknown>;
        if (camelizeResponse) {
          const getInviteCodesUrl = 'knockknock.UserInviteSrv/GetInviteCodes';
          if (url === getInviteCodesUrl) {
            const reg = /^(?=.*[0-9].*)(?=.*[A-Z].*)(?=.*[a-z].*).{6}$/;
            // eslint-disable-next-line no-param-reassign
            rsp.data = humps.camelizeKeys(data, (key, convert) => (reg.test(key) ? key : convert(key))) as CommonRsp;
          } else {
            // eslint-disable-next-line no-param-reassign
            rsp.data = humps.camelizeKeys(rsp.data) as CommonRsp;
          }
        }
        return rsp;
      }
      throw new RspError({
        type: NetErrorType.businessError,
        status: rsp.status,
        data: rsp.data,
        url: request.responseURL,
      });
    }, (err) => {
      if (err.name === 'RspError') {
        // 前面抛出的错误
        throw err;
      }
      if (err.response) {
        // 有回包的错误，status非200
        const request = err.request as XMLHttpRequest;
        if (err.response.status === 401) {
          // 鉴权失败
          throw new RspError({
            status: err.response.status,
            type: NetErrorType.authError,
            url: request.responseURL,
            data: err.response,
            message: err.message,
          });
        }
        throw new RspError({
          status: err.response.status,
          type: NetErrorType.serverError,
          data: err.response,
          url: request.responseURL,
          message: err.message,
        });
      }
      if (err.request) {
        // 请求已经发出，没有回包，可能是网络错误或者跨域问题

        const request = err.request as XMLHttpRequest;
        // 拿不到url，只能用aegis注入的url
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const url = request.responseURL || request.aegisUrl;
        const rspError = new RspError({
          status: request.readyState,
          type: NetErrorType.clientError,
          data: null,
          url,
          message: err.message,
        });
        // 这些错误无法处理，上报会导致误告警，所以忽略
        const ignoreKeywords = ['Request aborted', 'Network Error'];
        const isNoReport = ignoreKeywords.some(item => rspError.message.includes(item));
        if (isNoReport) {
          throw new NoReportError(rspError);
        }
        throw rspError;
      }
      // 代码错误
      throw new RspError({
        status: 0,
        type: NetErrorType.codeError,
        data: null,
        url: err.request?.requestURL,
        message: err.message,
      });
    });
  }

  /** 请求前数据处理钩子 */
  static beforeRequest(func: BeforeRequestHook): void {
    this.http.interceptors.request.use(options => ({
      ...options,
      ...func({
        headers: options.headers,
        url: options.url,
        method: options.method,
        baseURL: options.baseURL,
        params: options.params,
        data: options.data,
      }),
    }));
  }

  /** 响应数据处理钩子 */
  static afterResponseError(func: AfterResponseErrorHook): void {
    this.http.interceptors.response.use(undefined, func);
  }


  static async request<T extends CommonRsp, U>(options: NetRequestOptions<TRequest<U>>): Promise<T> {
    const path = options.path ?? `${this.appName}.${options.serverName}/${options.funcName}`;
    const req = options.data ? humps.decamelizeKeys(options.data as Record<string, unknown>) as unknown : null;
    const params = {
      ...(options.method === 'GET' ? req as TRequest<U> : null),
      'x-req-id': `0x4b-${Date.now()}-${Math.random()}`,
    };
    const r = await this.http.request<T, AxiosResponse<T, U>, TRequest<U>>({
      url: path,
      method: options.method ?? 'POST',
      withCredentials: true,
      data: req as TRequest<U>,
      params,
      headers: {
        'Content-type': options?.contentType ?? 'application/json',
      },
      // 自定义数据，要带到响应里
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      autoHandleError: options.autoHandleError ?? false,
      camelizeResponse: options.camelizeResponse ?? true,
    });
    return r.data;
  }

  /**
   * 串行请求，把分页列表全部请求出来
   * @param options 请求选项
   * @returns 响应数据
   */
  static async requestFullList<T extends CommonRsp, U>(options: NetRequestOptions<TRequest<U>>): Promise<T> {
    const data = options.data as unknown as INetRequestListReq;
    if (!data.page || !(data.pageSize ?? data.page_size)) {
      return Net.request<T, U>(options);
    }
    let isEnd = false;
    let response = {};
    while (!isEnd) {
      const result = await Net.request<T, U>(options) as unknown as INetRequestListRsp;
      response = mergeWith(response, result, customizer);
      isEnd = result.isEnd ?? result.is_end ?? true;
      if (!isEnd) {
        if (result.nextPage ?? result.next_page) {
          data.page = result.nextPage ?? result.next_page;
        } else {
          data.page = (data.page || 1) + 1;
        }
      }
    }
    return response as unknown as T;
  }
}
export default Net;
